import type { AthleteTargets } from "@heraion/shared";
import { useAthleteTargets, type UseAthleteTargetsOptions } from "@heraion/ui";

import { apiFetch } from "../api";
import { lsCache } from "../lsCache";

// ME-3e: cache-first localStorage adapter for the athlete's own weekly
// targets, mirroring mobile's AsyncStorage-backed cache (useAthleteTargets.ts)
// so a cold web load doesn't blank what reads them while the network
// round-trips. One adapter, one key: the Home composer, Progress and the
// Create hub (AP-9b) read the same document through the same cache, as
// mobile's useProgressDeps does.
const athleteTargetsLsCache = lsCache<AthleteTargets>("jl.athleteTargets.v1");

// One request in flight for every mount. AppShell renders the active tab and
// the Create hub together, each with its own hook instance, and the shared
// hook does not coalesce: without this, opening the hub over Progress or Home
// fired two GETs on the same key. Module state, like the ls caches above:
// no context needed. The hook only ever reads through this fetcher.
const inFlight = new Map<string, Promise<unknown>>();
const coalescedFetch: UseAthleteTargetsOptions["apiFetch"] = <T,>(
  path: string,
  opts?: Parameters<typeof apiFetch>[1],
): Promise<T> => {
  let pending = inFlight.get(path);
  if (pending === undefined) {
    pending = apiFetch<unknown>(path, opts).finally(() => inFlight.delete(path));
    inFlight.set(path, pending);
  }
  return pending as Promise<T>;
};

export function useSelfAthleteTargets() {
  return useAthleteTargets({ apiFetch: coalescedFetch, cache: athleteTargetsLsCache });
}
